
import type { DynamoItemType} from '../dto/itemTypes';
import { ItemType } from '../dto/itemTypes';

/**
* To be expanded with more item types.
*
* TODO: For now grabbing it from media type which is parsed from filename.
*       MIM-987: We should use FFprobe to determine type serverside instead or relying on filename
*/
export const itemTypeFromMediaType = (mediaType: string): DynamoItemType => {
  if (isImageType(mediaType)) {
    return ItemType.IMAGE;
  } else if(isVideoType(mediaType)) {
    return ItemType.VIDEO;
  } else if(isAudioType(mediaType)) {
    return ItemType.AUDIO;
  }
  return ItemType.FILE;
};

export const mediaTypeFromFilename = (filename: string, defaultMediaType: string = 'application/octet-stream'): string => {
  const pos = filename.lastIndexOf('.');
  if (pos === -1) {
    return defaultMediaType;
  }
  const ext = filename.slice(pos + 1).toLowerCase();
  return ext2MimeMap[ext] ?? defaultMediaType;
};

export const itemTypeFromFilename = (filename: string): DynamoItemType => {
  const mediaType = mediaTypeFromFilename(filename);
  return itemTypeFromMediaType(mediaType);
};

export const isSubtitleType = (mediaType: string | undefined) => {
  return startsWith(mediaType, 'subtitle');
};

// Only Supporting jpeg and png for poster frame now
export const isPosterFrameImageType = (mediaType: string | undefined) => {
  return mediaType === 'image/jpeg' || mediaType === 'image/png';
};

export const isImageType = (mediaType: string | undefined): boolean => {
  return startsWith(mediaType, 'image') && mediaType !== 'image/x-icon';
};

export const isVideoType = (mediaType: string): boolean => {
  return startsWith(mediaType, 'video') || startsWith(mediaType, 'movie') || mediaType.includes('mxf') || mediaType.includes('x-braw');
};

export const isAudioType = (mediaType: string | undefined): boolean => {
  return startsWith(mediaType, 'audio');
};

export const isPdfType = (mediaType: string | undefined): boolean => {
  return mediaType === 'application/pdf';
};

export const isMogrt = (fileName: string | undefined) => fileName ? fileName.slice(-6).toLowerCase() === '.mogrt' : false;

function startsWith(str: string | undefined, prefix: string) {
  if (typeof str !== 'string') {
    return false;
  }
  return str.startsWith(prefix);
}

const ext2MimeMap: Record<string, string> = {
  // video
  'mp4': 'video/mp4',
  'mov': 'video/quicktime',
  'qt': 'video/quicktime',
  'braw': 'video/quicktime',
  'avi': 'video/x-msvideo',
  'wmv': 'video/x-ms-wmv',
  'webm': 'video/webm',
  'mpeg': 'video/mpeg',
  'mpg': 'video/mpeg',
  'mpe': 'video/mpeg',
  'mp2v': 'video/mpeg',
  'm4v': 'video/mp4',
  'mkv': 'video/x-matroska',
  'mxf': 'application/mxf',
  'ogv': 'video/ogg',
  'ts': 'video/mp2t',
  '3gp': 'video/3gpp',
  '3g2': 'video/3gpp2',
  '360': 'video/mp4',
  'mts': 'video/mp2t',
  'm2ts': 'video/m2ts',
  'lrv': 'video/mp4',
  'dav': 'video/dav',

  // Could not find it. So just making something up to recognize as video.
  // TODO: Disabling R3D file recognition so that they are just ingested as non-video files until
  // commercial side of it is figured out.
  // Also, remove the temporary methods introduced in https://github.com/Mjoll/mimir/pull/14040
  // 'r3d': 'video/x-r3d',

  // image
  'jpg': 'image/jpeg',
  'jpeg': 'image/jpeg',
  'jfif': 'image/jpeg',
  'png': 'image/png',
  'bmp': 'image/bmp',
  'dng': 'image/x-adobe-dng',
  'jpe': 'image/jpeg',
  'tif': 'image/tiff',
  'tiff': 'image/tiff',
  'gif': 'image/gif',
  'svg': 'image/svg+xml',
  'webp': 'image/webp',
  'heic': 'image/heic',
  'heif': 'image/heif',
  'hif': 'image/heif',
  'cr2': 'image/cr2',
  'cr3': 'image/cr3',
  'arw': 'image/arw',
  'nef': 'image/nef',
  'tga': 'image/x-targa',
  'pbm': 'image/x-portable-bitmap',
  'pgm': 'image/x-portable-graymap',
  'ppm': 'image/x-portable-pixmap',
  'pnm': 'image/x-portable-anymap',
  'psd': 'image/vnd.adobe.photoshop',
  'xpm': 'image/x-xpixmap',
  'hdr': 'image/vnd.radiance',
  'pic': 'image/x-pict',
  'pict': 'image/x-pict',
  'pct': 'image/x-pict',
  'jps': 'image/x-jps',
  'pcd': 'image/pcd',
  'sgi': 'image/sgi',
  'raw': 'image/x-panasonic-raw',
  'rw2': 'image/x-panasonic-raw',
  'rwl': 'image/x-panasonic-raw',
  'raf': 'image/x-fuji-raf',
  'pef': 'image/x-pentax-pef',
  'orf': 'image/x-olympus-orf',
  'nrw': 'image/x-nikon-nrw',
  'jp2': 'image/jp2',
  'avif': 'image/avif',

  // audio
  'm4a': 'audio/mp4',
  'mp3': 'audio/mpeg',
  'mp2': 'audio/mpeg',
  'wav': 'audio/x-wav',
  'm3u': 'audio/x-mpegurl',
  'mid': 'audio/midi',
  'midi': 'audio/midi',
  'aac': 'audio/aac',
  'ac3': 'audio/ac3',
  'oga': 'audio/ogg',
  'ogg': 'audio/ogg',
  'opus': 'audio/opus',
  'weba': 'audio/webm',
  'aiff': 'audio/aiff',
  'aif': 'audio/aiff',
  'wma': 'audio/x-ms-wma',
  'flac': 'audio/flac',
  'au': 'audio/basic',

  // subtitle
  'srt': 'subtitle/srt',
  'stl': 'subtitle/stl',
  'vtt': 'subtitle/vtt',

  // others
  'json': 'application/json',
  'xml': 'application/xml',
  'rss': 'application/rss+xml',
  'mrss': 'application/rss+xml',
  'pdf': 'application/pdf',
};
